<template>
    <Modal
        :show="show"
        @close="$emit('close')"
    >
        <template #content>
            <div class="flex flex-col items-center gap-4">
                <div
                    class="p-3 rounded-full"
                    :class="iconBackgroundColor"
                >
                    <Icon
                        :name="iconName"
                        :class="iconClass"
                    />
                </div>
                <div class="space-y-2 py-4">
                    <div class="text-gray-800 text-center font-bold text-lg">
                        {{ title }}
                    </div>
                    <slot name="content" />
                </div>
            </div>
        </template>

        <template #actions>
            <Button
                @click="$emit('close')"
                color="light"
            >
                {{ closeActionText }}
            </Button>
            <Button
                v-if="action"
                @click="action()"
                :color="color"
                :iconName="actionIconName"
                :loading="actionProcessing"
            >
                {{ actionText }}
            </Button>
        </template>
    </Modal>
</template>

<script setup lang="ts">
    import { computed, type PropType } from 'vue'

    import Modal from '@/Shared/Component/Modal.vue'
    import Icon from '@/Shared/Component/Icon.vue'
    import Button from '@/Shared/Component/Button.vue'

    defineEmits(['close'])

    const props = defineProps({
        show: {
            type: Boolean,
            required: true,
        },
        title: {
            type: String,
            required: true,
        },
        color: {
            type: String as PropType<'danger' | 'info' | 'warning' | 'success'>,
            default: 'danger',
        },
        closeActionText: {
            type: String,
            default: 'Cancel',
        },
        action: {
            type: Function,
            default: null,
        },
        actionText: {
            type: String,
            default: 'Yes',
        },
        actionIconName: {
            type: String,
            default: '',
        },
        actionProcessing: {
            type: Boolean,
            default: false,
        },
    })

    const iconBackgroundColor = computed(() => {
        if (props.color === 'danger') {
            return 'bg-red-100'
        }

        if (props.color === 'info') {
            return 'bg-blue-100'
        }

        if (props.color === 'success') {
            return 'bg-green-100'
        }

        if (props.color === 'warning') {
            return 'bg-yellow-100'
        }

        return ''
    })

    const iconClass = computed(() => {
        if (props.color === 'danger') {
            return 'w-8 h-8 text-red-600'
        }

        if (props.color === 'info') {
            return 'w-8 h-8 text-blue-600'
        }

        if (props.color === 'success') {
            return 'w-8 h-8 text-green-600'
        }

        if (props.color === 'warning') {
            return 'w-8 h-8 text-yellow-500'
        }

        return ''
    })

    const iconName = computed(() => {
        if (props.color === 'danger') {
            return 'alert'
        }

        if (props.color === 'info') {
            return 'info-circle'
        }

        if (props.color === 'success') {
            return 'circle-check'
        }

        if (props.color === 'warning') {
            return 'close-circle'
        }

        return ''
    })
</script>
